<template>
	<div class="wrapper">
		<div class="container" :class="{ ireland: this.$store.getters.isIrl }">
			<h3 class="heading-4" :aria-label="$t('toCalculateNMIAbbr')">
				{{ $t('toCalculateNMI') }}
			</h3>
			<div class="head-wrapper">
				<h4
					class="heading-5"
					:aria-label="`${$t('GMI')} ${$options.filters.currency(grossIncome)}`"
				>
					{{ $t('GMI') }}
				</h4>

				<span aria-hidden="true">{{ grossIncome | currency }}</span>
			</div>
			<div class="content-wrapper">
				<ja-tax-button
					v-for="(tax, index) in taxes"
					:key="index"
					class="tax"
					:name="tax.name"
					:selected="tax.selected"
					@click.native="toggleTaxSelection(tax)"
				>
					<div class="button-content-wrapper">
						<svgicon v-if="!tax.selected" name="minus"></svgicon>
						{{ tax.name }}
						<span v-if="tax.selected" class="amount">{{
							tax.amount | currency
						}}</span>
					</div>
				</ja-tax-button>

				<div class="net-income">
					<div class="heading-5">{{ $t('yourNMIIs') }}</div>
					<span v-show="netIncomeRevealed">
						{{ netIncome | currency }}
					</span>
				</div>
			</div>
		</div>

		<div class="button-wrapper">
			<ja-button
				ref="button"
				class="small custom_left"
				name="prevLifeScenario"
				@click.native="goPrevStep"
			>
				<svgicon name="arrow-left"></svgicon>
				{{ $t('back') }}
			</ja-button>
			<slot></slot>
			<ja-button
				ref="button"
				class="small"
				name="nextLifeScenario"
				:aria-disabled="!netIncomeRevealed"
				:aria-label="$t('goBudgetSection')"
				@click.native="goNextStep"
			>
				{{ $t('next') }}
				<svgicon name="arrow-right"></svgicon>
			</ja-button>
		</div>
	</div>
</template>

<script>
import ProfileData from '../../mixins/ProfileData';
import '../../components/icons/minus';
import Button from '../../components/ui/Button';
import TaxButton from '../../components/ui/TaxButton';

export default {
	components: {
		jaTaxButton: TaxButton,
		jaButton: Button
	},
	mixins: [ProfileData],
	data() {
		return {
			selectedTax: false,
			selectedTaxesCount: 0
		};
	},

	computed: {
		stageSetup() {
			return this.$store.getters.stageSetupData;
		},
		grossIncome() {
			return this.stageSetup.grossMonthlyIncome;
		},
		taxes() {
			return [
				{
					name: this.$store.getters.isIrl
						? this.$t('paye')
						: this.stageSetup.federalIncomeTax.key,
					amount: this.stageSetup.federalIncomeTax.value,
					selected: false
				},
				{
					name: this.$store.getters.isIrl
						? this.$t('prsi')
						: this.stageSetup.medicareTax.key,
					amount: this.stageSetup.medicareTax.value,
					selected: false
				},
				{
					name: this.$store.getters.isIrl
						? this.$t('usc')
						: this.stageSetup.socialSecurityTax.key,
					amount: this.stageSetup.socialSecurityTax.value,
					selected: false
				}
			];
		},
		netIncome() {
			return this.profileData.netMonthlyIncome;
		},
		netIncomeRevealed() {
			return this.selectedTaxesCount == this.taxes.length;
		}
	},
	watch: {
		netIncomeRevealed(value) {
			if (value) {
				setTimeout(() => {
					this.$announcer.polite(
						this.$t('yourNMI', { amount: this.netIncome })
					);
				}, 200);
			}
		}
	},
	mounted() {
		this.$refs.button.$el.focus();
	},
	methods: {
		toggleTaxSelection(tax) {
			if (tax.selected) {
				tax.selected = false;
				this.selectedTaxesCount--;
				this.$announcer.polite(this.$t('taxUnselected', { tax: tax.name }));
			} else {
				tax.selected = true;
				this.selectedTaxesCount++;
				this.$announcer.polite(this.$t('taxSelected', { tax: tax.name }));
				setTimeout(() => {
					this.$announcer.polite(
						this.$t('taxAmount', { tax: tax.name, amount: tax.amount })
					);
				}, 100);
			}
		},
		goNextStep() {
			if (this.netIncomeRevealed) {
				this.$emit('goNextStep');
			}
		},
		goPrevStep() {
			this.$emit('goPrevStep');
		}
	}
};
</script>
<style lang="scss" scoped>
.wrapper {
	width: 100%;

	.container {
		margin: auto;
		width: 75%;
		margin: auto;
		padding: 32px 54px;
		margin-bottom: 32px;
		border-radius: 8px;
		background-color: #f3f8ff;

		@media screen and (min-width: 1280px) and (max-width: 1440px) {
			max-height: 35vh;
			overflow-y: auto;
		}

		&.ireland {
			@media screen and (min-width: 1280px) and (max-width: 1440px) {
				overflow-y: auto;
			}
		}

		h3 {
			margin-top: 0;
			margin-bottom: 32px;
		}

		.head-wrapper {
			display: flex;
			justify-content: space-between;
			padding-right: 15px;
			margin-left: 5px;
			h4 {
				text-transform: capitalize;
				color: $font-dark;
			}
			span {
				color: $questionnaire-color;
			}
		}
		.content-wrapper {
			display: flex;
			flex-direction: column;
			margin-top: 16px;

			.tax {
				margin-bottom: 8px;
				padding-right: 16px;
				padding-left: 16px;

				&:nth-child(3) {
					margin-bottom: 16px;
				}
				.button-content-wrapper {
					margin: auto;
					display: flex;
					font-family: 'RobotoRegular';
					font-size: 15px;
					line-height: 23px;
					letter-spacing: 0.5px;

					.svg-icon {
						margin-right: 10px;
						margin-left: 3%;
						width: 20px;
						height: 20px;
						@media (min-width: $breakpoint-mid) {
							margin-left: 16%;
						}
					}
					.amount {
						display: none;
					}
				}
			}
			.net-income {
				display: flex;
				height: 54px;
				padding-top: 24px;
				justify-content: space-between;
				align-items: center;
				border-top: 1px solid $checkbox-color;
				.heading-5 {
					text-transform: capitalize;
					color: $font-dark;
				}
				span {
					font-family: 'RobotoRegular';
					color: $font-header;
					@media (min-width: $breakpoint-mid) {
						font-size: 24px;
					}
				}
			}
		}
	}
}
/* button {
	&.selected {
		::v-deep svg {
			display: none;
		}
	}
}
button {
	&.selected {
		::v-deep .amount {
			display: inline !important;
			color: $questionnaire-color;
		}
	}
}
button {
	&.selected {
		::v-deep .button-content-wrapper {
			display: flex;
			justify-content: space-between;
		}
	}
} */
</style>
